// SMS character/segment counting — mirrors how BulkSMSBD (and every GSM
// carrier) actually bills a message: plain GSM 03.38 text gets 160 chars
// in a single segment (153/segment once it needs to be split across
// several, since each part loses 7 chars to the concatenation header);
// the moment a message contains ANY character outside that set — Bangla
// script, emoji, curly quotes, etc. — the whole message downgrades to
// UCS-2/Unicode encoding, which only fits 70 chars per segment (67/segment
// when split). Getting this wrong (e.g. always assuming 160) would show
// staff a "1 SMS" estimate for a message that's actually billed as 2-3.
//
// GSM 7-bit "basic" set (each character = 1 unit) — see 3GPP TS 23.038.
const GSM_7BIT_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
// GSM 7-bit "extension" set — each of these is really an ESC + char pair,
// so it costs 2 units of the 160/153 budget, not 1.
const GSM_7BIT_EXT = '^{}\\[~]|€';

export type SmsEncoding = 'GSM-7' | 'Unicode';

export interface SmsAnalysis {
  encoding: SmsEncoding;
  /** Billed length: GSM-7 units (extension chars count double) or UCS-2 code points. */
  length: number;
  /** Per-segment character budget at the current segment count (160/153 or 70/67). */
  segmentLimit: number;
  /** How many SMS segments this message will be billed as (0 for an empty message). */
  segments: number;
}

export function analyzeSms(text: string): SmsAnalysis {
  if (text.length === 0) {
    return { encoding: 'GSM-7', length: 0, segmentLimit: 160, segments: 0 };
  }

  let isGsm7 = true;
  let gsmLength = 0;
  for (const ch of text) {
    if (GSM_7BIT_BASIC.includes(ch)) {
      gsmLength += 1;
    } else if (GSM_7BIT_EXT.includes(ch)) {
      gsmLength += 2;
    } else {
      isGsm7 = false;
      break;
    }
  }

  if (isGsm7) {
    const singleLimit = 160;
    const multiLimit = 153;
    const segments = gsmLength <= singleLimit ? 1 : Math.ceil(gsmLength / multiLimit);
    return { encoding: 'GSM-7', length: gsmLength, segmentLimit: segments > 1 ? multiLimit : singleLimit, segments };
  }

  // Non-GSM-7 character found (e.g. Bangla script) — the whole message
  // encodes as UCS-2, counted in Unicode code points (so a 4-byte emoji
  // still counts as 1 "character", matching how carriers bill it).
  const length = Array.from(text).length;
  const singleLimit = 70;
  const multiLimit = 67;
  const segments = length <= singleLimit ? 1 : Math.ceil(length / multiLimit);
  return { encoding: 'Unicode', length, segmentLimit: segments > 1 ? multiLimit : singleLimit, segments };
}
